'use client'
import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Spinner, Tooltip, cn} from '@heroui/react'
import {usePathname, useRouter} from 'next/navigation'
import Link from 'next/link'

import Box from '@/components/UI/Box/Box'
import AmyInput from '@/components/UI/AmyInput/AmyInput'
import {Body6} from '@/components/UI/Typography'
import {AdvisorCardProps} from '@/components/AdvisorCards/types'
import AmyImage from '@/components/UI/AmyImage/AmyImage'

import Icon from '../UI/Icon/Icon'
import {useDebouncedCallback} from '../../src/shared/utils/useDebounce'

import {PageableInterface} from '@/src/services/SearchService/types'
import {truncateText} from '@/src/shared/utils/truncateText'
import {advisorSearchWithKey} from '@/src/services/SearchService/services'
import {formatNumber} from '@/src/shared/utils/formatNumber'

const NavSearch = () => {
  const router = useRouter()
  const [searchKey, setSearchKey] = useState('')
  const [searchResults, setSearchResults] =
    useState<PageableInterface<AdvisorCardProps>>()
  const [isVisibleSearchResult, setIsVisibleSearchResult] = useState(false)
  const [loading, setLoading] = useState(false)
  const outsideSearchRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const timer = useRef<any>(null)
  const pathname = usePathname()

  const handleClickSearchOutside = useCallback((event: MouseEvent) => {
    if (
      outsideSearchRef.current &&
      !outsideSearchRef.current.contains(event.target as Node)
    ) {
      timer.current = setTimeout(() => setIsVisibleSearchResult(false), 50)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mouseup', handleClickSearchOutside)
    return () => {
      document.removeEventListener('mouseup', handleClickSearchOutside)
      clearTimeout(timer.current)
    }
  }, [handleClickSearchOutside])

  useEffect(() => {
    setIsVisibleSearchResult(false)
    setSearchKey('')
  }, [pathname])

  const makeSearchRequest = async (str: string) => {
    setLoading(true)
    setIsVisibleSearchResult(true)
    setSearchKey(str)
    advisorSearchWithKey(str, 5, 0)
      .then(res => {
        setSearchResults(res)
      })
      .finally(() => setLoading(false))
  }

  const search = (event: React.SyntheticEvent) => {
    if ((event as React.KeyboardEvent).key === 'Enter') {
      makeSearchRequest(searchKey)
        .then(() => router.push(`/search/${searchKey}`))
        .then(() => setIsVisibleSearchResult(false))
    }
  }

  const debouncedSearch = useDebouncedCallback(
    (str: string) => makeSearchRequest(str),
    800
  )

  return (
    <Box
      ref={wrapperRef}
      className="justify-center relative w-full"
      onClick={() => makeSearchRequest(searchKey)}>
      <Tooltip
        isOpen={isVisibleSearchResult}
        style={{
          width: wrapperRef.current?.offsetWidth,
        }}
        className={cn('ml-[12px] border-1 border-primary rounded-lg')}
        placement="bottom"
        offset={16}
        content={
          <Box ref={outsideSearchRef} className="w-full flex-col gap-2 p-2">
            {searchResults?.content.map(element => (
              <Link
                key={element.id}
                href={`/danisman/${element.slug}`}
                className="block hover:bg-gray-100 rounded p-2"
                style={{opacity: 0.9}}>
                <Box className="items-center justify-between text-text w-full">
                  <Box className="items-center gap-2 flex-grow">
                    <Box className="h-[50px] w-[50px] min-w-[50px] min-h-[50px] relative">
                      <AmyImage
                        skeletonProps={{
                          className: 'rounded-lg',
                        }}
                        alt={element.name + ' ' + element.lastname}
                        src={element.imageUrl}
                        fill
                        className="rounded-lg"
                      />
                    </Box>
                    <Body6>
                      {truncateText(element.title, 30)} - {element.name}{' '}
                      {element.lastname}
                    </Body6>
                  </Box>
                  <Box className="items-center gap-2 flex-shrink-0">
                    {element.price === 0 ||
                    element.price === element.salePrice ? (
                      <Body6>{element.price} ₺</Body6>
                    ) : (
                      <>
                        <Body6 className="line-through">
                          {formatNumber(element.price)} ₺
                        </Body6>
                        <Body6 className="text-primary">
                          {formatNumber(element.salePrice)} ₺
                        </Body6>
                      </>
                    )}
                  </Box>
                </Box>
              </Link>
            ))}
            {!loading ? (
              <Link
                key={'last'}
                href={`/search/${searchKey === null ? '' : searchKey}`}
                className="block text-center py-2 text-primary hover:underline">
                {!!searchResults?.content.length
                  ? `Tüm Sonuçları Gör (${searchResults?.totalElements})`
                  : 'Sonuç Bulunamadı'}
              </Link>
            ) : (
              <Box className="flex justify-center py-2">
                <Spinner size="sm" />
              </Box>
            )}
          </Box>
        }>
        <AmyInput
          fullWidth
          variant="flat"
          classNames={{inputWrapper: 'rounded-lg', input: 'outline-none'}}
          className={
            isVisibleSearchResult
              ? 'border-1 border-primary rounded-lg m-0'
              : 'rounded-lg m-[1px]'
          }
          aria-label="navbar-search-bar-1"
          placeholder="Danışmanlık Uzmanlık... Ara"
          onKeyDown={search}
          onChange={value => {
            setSearchKey(value)
            debouncedSearch(value)
          }}
          value={searchKey}
          endContent={<Icon name="search" className="fill-primary" />}
        />
      </Tooltip>
    </Box>
  )
}

export default NavSearch
